import { Badge, Flex, Table, Text } from '@chakra-ui/react';
import { memo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { LuTriangleAlert } from 'react-icons/lu';

import CollectionProgress from '@features/finance/CollectionProgress';
import PaymentRequestStatusBadge from '@features/finance/PaymentRequestStatusBadge';
import { formatLocal, isAwaitingManualPayout, remainingAmount } from '@features/finance/financeMeta';
import type { PaymentRequest } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';

interface Props {
  requests: PaymentRequest[];
}

interface RowProps {
  request: PaymentRequest;
  index: number;
  totalCount: number;
  onRowClick: (id: string) => () => void;
}

const PaymentLinkRow = memo(({ request, index, totalCount, onRowClick }: RowProps) => {
  const remaining = remainingAmount(request.targetAmount, request.receivedAmount);
  const awaitingPayout = isAwaitingManualPayout(request);
  return (
    <Table.Row
      fontSize="sm"
      cursor="pointer"
      onClick={onRowClick(request.id)}
      bg={awaitingPayout ? { base: 'orange.50', _dark: 'orange.950' } : undefined}
    >
      <Table.Cell
        border="none"
        p={4}
        borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
        borderBottomLeftRadius={index === totalCount - 1 ? 'xl' : 'none'}
      >
        <Flex direction="column" gap={0.5}>
          <Text fontWeight="medium">{request.title}</Text>
          <Text fontSize="xs" color="text.secondary">
            {request.customerName}
          </Text>
        </Flex>
      </Table.Cell>
      <Table.Cell border="none" p={4} hideBelow="md">
        <Flex direction="column" gap={1.5} minW="140px">
          <CollectionProgress target={request.targetAmount} received={request.receivedAmount} />
          <Text fontSize="xs" color="text.secondary">
            {formatLocal(request.receivedAmount, request.targetCurrency)} of{' '}
            {formatLocal(request.targetAmount, request.targetCurrency)}
          </Text>
        </Flex>
      </Table.Cell>
      <Table.Cell border="none" p={4} textAlign="end">
        <Text fontWeight="medium">{formatLocal(remaining, request.targetCurrency)}</Text>
      </Table.Cell>
      <Table.Cell
        border="none"
        p={4}
        borderTopRightRadius={index === 0 ? 'xl' : 'none'}
        borderBottomRightRadius={index === totalCount - 1 ? 'xl' : 'none'}
      >
        <Flex direction="column" alignItems="flex-end" gap={1}>
          <PaymentRequestStatusBadge status={request.status} />
          {awaitingPayout && (
            <Badge size="xs" colorPalette="orange" variant="solid" borderRadius="full">
              <LuTriangleAlert size={11} />
              Pay supplier
            </Badge>
          )}
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
});
PaymentLinkRow.displayName = 'PaymentLinkRow';

const PaymentLinksTable = memo(({ requests }: Props) => {
  const router = useRouter();
  const { paymentLinkHref } = useHref();

  const onRowClick = useCallback(
    (id: string) => () => {
      router.push(paymentLinkHref(id));
    },
    [router, paymentLinkHref],
  );

  if (requests.length === 0) {
    return (
      <Flex
        direction="column"
        bg="surface.muted"
        borderRadius="xl"
        p={10}
        alignItems="center"
        gap={1}
      >
        <Text fontWeight="medium">No payment links here yet</Text>
        <Text fontSize="sm" color="text.secondary">
          Create a link to start collecting from a client.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2}>
      <Table.Root>
        <Table.Header fontSize="sm" fontWeight="medium">
          <Table.Row bg="transparent">
            <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4}>
              Link
            </Table.ColumnHeader>
            <Table.ColumnHeader
              border="none"
              color="text.secondary"
              fontWeight="medium"
              p={4}
              hideBelow="md"
            >
              Collected
            </Table.ColumnHeader>
            <Table.ColumnHeader
              border="none"
              color="text.secondary"
              fontWeight="medium"
              p={4}
              textAlign="end"
            >
              Remaining
            </Table.ColumnHeader>
            <Table.ColumnHeader
              border="none"
              color="text.secondary"
              fontWeight="medium"
              p={4}
              textAlign="end"
            >
              Status
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {requests.map((request, index) => (
            <PaymentLinkRow
              key={request.id}
              request={request}
              index={index}
              totalCount={requests.length}
              onRowClick={onRowClick}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
});
PaymentLinksTable.displayName = 'PaymentLinksTable';

export default PaymentLinksTable;
